import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";

/**
 * Composable for tracking document upload events in performance review dialogs.
 *
 * Each event is emitted under a single static name (see AmplitudeEventNames); the originating
 * dialog ("review_document" or "already_done") is carried in the `dialog` property rather than
 * baked into the event name. This fixes the previous runtime-built names (ENG-1178 §1).
 *
 * Property names follow the backend convention used by the server-side
 * `performance_review_document_uploaded` event: `review_for` and `upload_type(s)`.
 *
 * The "uploaded" event is intentionally NOT emitted here — it is owned by the server-side
 * link_documents endpoint (single canonical event). See ENG-1224.
 *
 * @param {string} dialog - Which upload dialog this is ("review_document" or "already_done")
 * @param {string} reviewId - The performance review ID
 * @param {Ref<string>} reviewType - The type of review ("self" / "connection" / "connections")
 */
export const usePerformanceReviewDocumentEvents = (dialog, reviewId, reviewType) => {
    const logEvent = (eventName, properties = {}) => {
        // Only skip logging when review_type is genuinely missing (the original intent of this
        // guard — see ENG-652 / PR #4602). The previous `reviewType.value in REVIEW_TYPES` check
        // tested object *keys* ("SELF"/"CONNECTIONS"), never the values, so it dropped every event.
        if (reviewType.value) {
            logUserInteraction(eventName, { dialog, ...properties });
        }
    };

    const handleDocumentSelected = (document) => {
        logEvent(AmplitudeEventNames.PERFORMANCE_REVIEW_DOCUMENT_SELECTED, {
            review_id: reviewId,
            review_for: reviewType.value,
            upload_type: document.upload_type,
        });
    };

    const handleDocumentUploadError = (document, pasted) => {
        logEvent(AmplitudeEventNames.PERFORMANCE_REVIEW_DOCUMENT_UPLOAD_FAILED, {
            review_id: reviewId,
            review_for: reviewType.value,
            upload_type: document.upload_type,
            pasted: pasted,
        });
    };

    const handleDocumentRemoved = (document) => {
        logEvent(AmplitudeEventNames.PERFORMANCE_REVIEW_DOCUMENT_REMOVED, {
            review_id: reviewId,
            review_for: reviewType.value,
            upload_type: document.upload_type,
        });
    };

    const handleSubmitted = (documents = []) => {
        // A submission can contain multiple documents of different types, so upload_types
        // carries the distinct types (mirrors the server-side upload_types list).
        const uploadTypes = [...new Set(documents.map((doc) => doc.upload_type).filter(Boolean))];
        logEvent(AmplitudeEventNames.PERFORMANCE_REVIEW_DOCUMENT_SUBMITTED, {
            review_id: reviewId,
            review_for: reviewType.value,
            upload_types: uploadTypes,
        });
    };

    return {
        handleDocumentSelected,
        handleDocumentUploadError,
        handleDocumentRemoved,
        handleSubmitted,
    };
};
